import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { X, Copy, Sparkle, Gift, ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

const PROMO_CODE = 'AGENCYMARKET';
const SEEN_KEY = 'agency_market_promo_seen_v1';

// Routes where the promo modal should NEVER appear (it blocks form submits)
const BLOCKED_ROUTES = ['/login', '/register', '/admin', '/checkout', '/forgot-password'];

export const WelcomePromoModal = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isBlockedRoute = BLOCKED_ROUTES.some((r) => location.pathname.startsWith(r));

  useEffect(() => {
    if (isBlockedRoute) {
      // Make sure we never linger when navigating to an auth/checkout route
      setOpen(false);
      return undefined;
    }
    const seen = sessionStorage.getItem(SEEN_KEY);
    if (!seen) {
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isBlockedRoute, location.pathname]);

  const handleClose = () => {
    sessionStorage.setItem(SEEN_KEY, 'true');
    setOpen(false);
  };

  // ESC and outside click should dismiss the modal
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      toast.success('¡Código copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('No se pudo copiar');
    }
  };

  const handleUseNow = () => {
    sessionStorage.setItem(SEEN_KEY, 'true');
    sessionStorage.setItem('apply_coupon', PROMO_CODE);
    setOpen(false);
    navigate('/checkout');
  };

  if (isBlockedRoute) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-0 bg-transparent shadow-none"
        data-testid="welcome-promo-modal"
        onPointerDownOutside={handleClose}
        onEscapeKeyDown={handleClose}
      >
        <VisuallyHidden.Root asChild>
          <div>
            <DialogTitle>Promoción con Descuento — Agency Market</DialogTitle>
            <DialogDescription>
              Aprovechá hasta 33% OFF con el código AGENCYMARKET
            </DialogDescription>
          </div>
        </VisuallyHidden.Root>
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0066FF] via-[#0084FF] to-[#00E676] p-[2px] animate-pulse-glow">
          <div className="relative bg-black rounded-2xl p-8 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#00E676] rounded-full blur-3xl opacity-30" />
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#0066FF] rounded-full blur-3xl opacity-40" />

            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
              data-testid="close-promo-modal"
              aria-label="Cerrar"
            >
              <X size={16} className="text-white" />
            </button>

            <div className="relative z-10">
              <div className="flex items-center justify-center mb-4">
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0066FF] to-[#00E676] flex items-center justify-center">
                    <Gift size={32} weight="fill" className="text-white" />
                  </div>
                  <Sparkle
                    size={20}
                    weight="fill"
                    className="absolute -top-1 -right-1 text-[#00E676] animate-pulse"
                  />
                </div>
              </div>

              <div className="text-center mb-6">
                <div className="inline-block px-3 py-1 rounded-full bg-[#00E676]/20 border border-[#00E676]/50 mb-3">
                  <span className="text-xs font-bold tracking-widest text-[#00E676] uppercase">
                    Oferta exclusiva
                  </span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2 leading-tight">
                  Hasta <span className="text-gradient-brand">33% OFF</span>
                  <br />
                  en todos los servicios
                </h2>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Aprovechá los precios rebajados por tiempo limitado. <br />
                  Seguidores, likes y vistas con descuento.
                </p>
              </div>

              <div className="mb-6">
                <p className="text-xs text-gray-400 text-center mb-2 uppercase tracking-wider">
                  Usá el código
                </p>
                <button
                  onClick={handleCopy}
                  className="w-full group relative rounded-xl border-2 border-dashed border-[#00E676]/60 hover:border-[#00E676] bg-[#00E676]/5 hover:bg-[#00E676]/10 transition-all duration-200 p-4"
                  data-testid="copy-promo-code"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-white tracking-widest">
                      {PROMO_CODE}
                    </span>
                    {copied ? (
                      <CheckCircle size={24} weight="fill" className="text-[#00E676]" />
                    ) : (
                      <Copy size={20} className="text-gray-400 group-hover:text-[#00E676] transition-colors" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 text-left">
                    {copied ? '¡Copiado!' : 'Tocá para copiar'}
                  </p>
                </button>
              </div>

              <Button
                onClick={handleUseNow}
                size="lg"
                className="w-full bg-gradient-to-r from-[#0066FF] to-[#00E676] hover:opacity-90 text-white font-semibold border-0"
                data-testid="use-promo-now"
              >
                Usar ahora
                <ArrowRight size={20} className="ml-2" />
              </Button>

              <p className="text-center text-xs text-gray-500 mt-4">
                * Válido en seguidores, likes y vistas de Instagram, TikTok y Facebook
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
